'use client';

import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Calendar } from './calendar';
import { SelectTriggerAsChild } from './selectTriggerAsChild';
import { DateUtils } from '@jaam/commons';

interface DatePickerProps {
  value?: Date | string;
  onChange?: (date: Date | undefined) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  isLoading?: boolean; // Loading state
  validationState?: 'success' | 'error' | 'none'; // New prop for green/red styling
  isDirty?: boolean;
  className?: string;
  type?: 'date' | 'month';
  dateFormat?: string;
  disabled?: boolean;
  required?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Date',
  error,
  isLoading,
  validationState,
  isDirty,
  className,
  type,
  dateFormat,
  required,
  disabled,
  inputSize = 'md',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleSelect = (date: Date | undefined) => {
    const parsedDate = date
      ? DateUtils(date).add(1, 'hour').toDate() //to avoid day backward issues
      : undefined;

    if (onChange) onChange(parsedDate);
    setIsOpen(false);
  };

  return (
    <div className={`space-y-2 ${className ? className : ''}`}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <SelectTriggerAsChild
            label={`${placeholder}${required ? '*' : ''}`}
            isLoading={isLoading}
            validationState={validationState}
            isDirty={isDirty}
            value={value ? DateUtils(value).format('DD/MM/YYYY') : undefined}
            error={error}
            asCalendar
            onClick={() => setIsOpen(!isOpen)} // Toggle popover on click
            disabled={disabled}
            inputSize={inputSize}
          >
            {value ? DateUtils(value).format('DD/MM/YYYY') : null}
          </SelectTriggerAsChild>
        </PopoverTrigger>
        <PopoverContent className="p-0 min-w-[350px]" align="start">
          <Calendar
            mode="single"
            selected={value as Date}
            onSelect={handleSelect}
            initialFocus
            disabled={disabled}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};
